import { useEffect, useRef, useState } from 'react';
import { Leaf, Bug, ArrowRight, Shield, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const products = [
  {
    title: 'Plant Identifier',
    description:
      'Instantly identify any plant species with our AI-powered recognition system. Get detailed information about care requirements and growing tips.',
    features: [
      'Accurate species identification',
      'Detailed care instructions',
      'Growing tips and tricks',
      'Plant history tracking'
    ],
    image:
      'https://images.unsplash.com/photo-1520412099551-62b6bafeb5bb?auto=format&fit=crop&w=800&h=600&q=80',
    icon: Leaf,
    link: '/identifier',
    theme: {
      iconBg: 'bg-emerald-100',
      icon: 'text-emerald-600',
      button: 'bg-emerald-600 hover:bg-emerald-700',
      featureIcon: CheckCircle,
      featureIconColor: 'text-emerald-600'
    }
  },
  {
    title: 'Disease Detection',
    description:
      'Early detection of plant diseases using advanced AI analysis. Get treatment recommendations and prevent the spread of diseases.',
    features: [
      'Disease identification',
      'Treatment recommendations',
      'Prevention tips',
      'Disease history tracking'
    ],
    image:
      'https://images.unsplash.com/photo-1615011195789-03647e6331eb?auto=format&fit=crop&w=800&h=600&q=80',
    icon: Bug,
    link: '/disease-detection',
    theme: {
      iconBg: 'bg-amber-100',
      icon: 'text-amber-600',
      button: 'bg-amber-600 hover:bg-amber-700',
      featureIcon: Shield,
      featureIconColor: 'text-amber-600'
    }
  }
];

function ProductCard({ product, index, isLoaded }) {
  const cardRef = useRef(null);
  const imageRef = useRef(null);
  const contentRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    const image = imageRef.current;
    const content = contentRef.current;
    const features = featuresRef.current;

    if (!card || !image || !content || !features) return;

    // Clear any existing ScrollTriggers for this card
    ScrollTrigger.getAll().forEach(st => {
      if (st.vars.trigger === card) {
        st.kill();
      }
    });

    // Set initial states
    gsap.set(card, { opacity: 1 }); // Start visible
    gsap.set(image, { opacity: 1, scale: 1 }); // Start visible
    gsap.set(content.children, { opacity: 1 }); // Start visible
    gsap.set(features.children, { opacity: 1 }); // Start visible

    // Create the scroll-triggered animation
    const scrollTl = gsap.timeline({
      scrollTrigger: {
        trigger: card,
        start: 'top bottom-=100',
        end: 'center center',
        toggleActions: 'play none none reverse'
      }
    });

    scrollTl
      .from(card, {
        opacity: 0,
        y: 50,
        duration: 0.8,
        ease: 'power3.out'
      })
      .from(
        image,
        {
          opacity: 0,
          scale: 1.1,
          duration: 1,
          ease: 'power2.out'
        },
        '-=0.4'
      )
      .from(
        content.children,
        {
          opacity: 0,
          y: 20,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out'
        },
        '-=0.6'
      )
      .from(
        features.children,
        {
          opacity: 0,
          x: index % 2 === 0 ? -20 : 20,
          duration: 0.4,
          stagger: 0.05,
          ease: 'power2.out'
        },
        '-=0.4'
      );

    // Hover animation
    const hoverTl = gsap.timeline({ paused: true });
    hoverTl
      .to(image, {
        scale: 1.05,
        duration: 0.3,
        ease: 'power2.out'
      })
      .to(
        features.children,
        {
          x: 5,
          duration: 0.2,
          stagger: 0.03,
          ease: 'power2.out'
        },
        0
      );

    // Add hover event listeners
    const handleEnter = () => hoverTl.play();
    const handleLeave = () => hoverTl.reverse();

    card.addEventListener('mouseenter', handleEnter);
    card.addEventListener('mouseleave', handleLeave);

    return () => {
      card.removeEventListener('mouseenter', handleEnter);
      card.removeEventListener('mouseleave', handleLeave);
      ScrollTrigger.getAll().forEach(st => {
        if (st.vars.trigger === card) {
          st.kill();
        }
      });
    };
  }, [index, isLoaded]);

  return (
    <div
      ref={cardRef}
      className={`bg-white rounded-2xl shadow-xl overflow-hidden transform ${
        index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
      } lg:flex`}
    >
      <div className="relative lg:w-1/2 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-black/20 z-10" />
        <div ref={imageRef} className="w-full h-64 lg:h-full">
          <img
            src={product.image}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div
          className={`absolute top-4 left-4 ${product.theme.iconBg} p-3 rounded-xl z-20`}
        >
          <product.icon className={`w-6 h-6 ${product.theme.icon}`} />
        </div>
      </div>

      <div className="p-8 lg:w-1/2 lg:flex lg:flex-col lg:justify-center">
        <div ref={contentRef}>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {product.title}
          </h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            {product.description}
          </p>
        </div>

        <ul ref={featuresRef} className="space-y-3 mb-8">
          {product.features.map((feature, i) => (
            <li key={i} className="flex items-center gap-3">
              <product.theme.featureIcon
                className={`w-5 h-5 flex-shrink-0 ${product.theme.featureIconColor}`}
              />
              <span className="text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        <Link
          to={product.link}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors text-white ${product.theme.button}`}
        >
          Try {product.title}
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
}

export function ProductShowcase() {
  const titleRef = useRef(null);

  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);

    const title = titleRef.current;
    if (!title) return;

    // Clear any existing ScrollTriggers
    ScrollTrigger.getAll().forEach(st => {
      if (st.vars.trigger === title) {
        st.kill();
      }
    });

    // Set initial state
    gsap.set(title, { opacity: 1 }); // Start visible

    // Create the heading animation
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: title,
        start: 'top bottom-=100',
        toggleActions: 'play none none reverse'
      }
    });

    // Split text and animate
    const words = title.textContent.split(' ');
    title.innerHTML = words
      .map(word => `<span class="inline-block">${word} </span>`)
      .join('');

    const spans = title.querySelectorAll('span');
    tl.from(spans, {
      opacity: 0,
      y: 30,
      duration: 0.8,
      stagger: 0.1,
      ease: 'power3.out'
    });

    return () => {
      ScrollTrigger.getAll().forEach(st => {
        if (st.vars.trigger === title) {
          st.kill();
        }
      });
    };
  }, [isLoaded]);

  return (
    <section className="relative py-24 bg-white">
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/30 via-white to-amber-50/30">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0,0,0,0.1) 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2
            ref={titleRef}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Powerful Tools for Plant Care
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From identification to disease detection, we provide comprehensive
            solutions for all your plant care needs
          </p>
        </div>

        <div className="space-y-12">
          {products.map((product, index) => (
            <ProductCard
              key={index}
              product={product}
              index={index}
              isLoaded={isLoaded}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
